import React from 'react';
import { useForm } from '../../../../../utils/validators';
import { CounterLabel, Button, Modal, Message, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { undefined_or_null_or_value, toFloat } from '../../../../../utils/tools';
import ColorPicker from '../../../../../components/color-picker';

import Styles from './editModal.module.css';

const MAP_TYPE_STYLE = {
    schematic: {
        opacity: 0,
        hover_opacity: 0,
        unavailable_opacity: 0.8,
        unavailable_color: 'white',
        unavailable_hover_opacity: 0.8,
        unavailable_hover_color: 'white',
        stroke_width: 0,
        unavailable_stroke_width: 0,
        hover_stroke_width: 0,
        unavailable_hover_stroke_width: 0
    },
    realistic: {
        opacity: 0.4,
        hover_opacity: 0.7,
        unavailable_opacity: 0,
        unavailable_color: 'white',
        unavailable_hover_opacity: 0,
        unavailable_hover_color: 'white',
        stroke_width: 0,
        unavailable_stroke_width: 0,
        hover_stroke_width: 0,
        unavailable_hover_stroke_width: 0
    }
};

const DEFAULT_STYLE = {
    color: null,
    hover_color: null,
    stroke: null,
    stroke_width: null,
    hover_stroke: null,
    hover_stroke_width: null,
    opacity: null,
    hover_opacity: null
};

export default function ZoneEditModal({ zone = null, onSubmit = () => {}, parent, editModalOpen, setEditModalOpen }) {
    const [styleForm, setStyle, setStyleForm] = useForm({ ...DEFAULT_STYLE });
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [showPickerColor, setShowPickerColor] = React.useState(false);
    const [showPickerStroke, setShowPickerStroke] = React.useState(false);
    const [showPickerHoverColor, setShowPickerHoverColor] = React.useState(false);
    const [showPickerHoverStroke, setShowPickerHoverStroke] = React.useState(false);

    const reset = React.useCallback(() => {
        const style = { ...DEFAULT_STYLE };
        setStyleForm({ ...style, ...MAP_TYPE_STYLE[parent.seatlib_data.map_type] });
    }, [MAP_TYPE_STYLE, DEFAULT_STYLE, parent]);

    const handleSubmit = React.useCallback(() => {
        onSubmit(styleForm);
        toast.success(t('ticket.charts.custom.chart.edit-toast-success'));
        setEditModalOpen(false);
    }, [onSubmit, styleForm, toast]);

    const hasDifferentKeyAndValue = React.useCallback((obj_1, obj_2) => {
        for (const key of Object.keys(obj_1)) {
            if (undefined_or_null_or_value(obj_1[key]) !== undefined_or_null_or_value(obj_2[key])) {
                return true;
            }
        }
        return false;
    }, []);

    // Allow to disabled the button if the form style matchs the default style for zone
    const disabled = React.useMemo(() => {
        const style = MAP_TYPE_STYLE[parent.seatlib_data.map_type];
        return !hasDifferentKeyAndValue(styleForm, style);
    }, [styleForm, parent, hasDifferentKeyAndValue]);

    // allow to display a warning if changes have been done on the style
    const displaySaveWarning = React.useMemo(() => {
        if (!zone) {
            return false;
        }
        return hasDifferentKeyAndValue(styleForm, zone.seatlib_data);
    }, [zone, styleForm]);

    React.useEffect(() => {
        if (!zone) {
            return;
        }
        const style = {
            color: zone.seatlib_data?.color,
            hover_color: zone.seatlib_data?.hover_color,
            stroke: zone.seatlib_data?.stroke,
            stroke_width: zone.seatlib_data?.stroke_width,
            hover_stroke: zone.seatlib_data?.hover_stroke,
            hover_stroke_width: zone.seatlib_data?.hover_stroke_width,
            opacity: zone.seatlib_data?.opacity,
            hover_opacity: zone.seatlib_data?.hover_opacity
        };
        setStyleForm(style);
    }, [zone]);

    return (
        <Modal
            allowExternalClosing={false}
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            submitButton={<Button primary label={t('ticket.common.action.validate')} onClick={handleSubmit} />}
            cancelButton={<Button primary inverted disabled={disabled} onClick={reset} label={t('ticket.charts.zone_editor.sidebar.cta.reset')} />}
            title={t('ticket.charts.custom.chart.modal-title')}
        >
            <div>
                {displaySaveWarning && (
                    <Message type="warning" margin={false}>
                        {t('ticket.charts.zone_editor.sidebar.warning')}
                    </Message>
                )}
                <div className={Styles['color-container']}>
                    <div className={Styles['color-title']}>{t('ticket.charts.zone_editor.sidebar.title')}</div>
                    <div className={Styles['color-picker-container']}>
                        <div className={Styles['color-picker']}>
                            <div className={Styles['color-picker-title-container']}>
                                <div
                                    className={Styles['color-picker-title']}
                                    onClick={() => setShowPickerColor(!showPickerColor)}
                                    style={{
                                        backgroundColor: styleForm['color'] || '#FFF'
                                    }}
                                />
                                <span>{t('ticket.charts.zone_editor.sidebar.form.color_label')}</span>
                            </div>
                            <ColorPicker
                                className="color-picker-container-block"
                                show={showPickerColor}
                                disableAlpha
                                onChange={color => setStyle('color', color.hex)}
                                onClickAway={() => setShowPickerColor(false)}
                                color={styleForm['color'] || '#FFF'}
                            />
                        </div>
                        <div className={Styles['color-picker']}>
                            <div className={Styles['color-picker-title-container']}>
                                <div
                                    className={Styles['color-picker-title']}
                                    onClick={() => setShowPickerStroke(!showPickerStroke)}
                                    style={{
                                        backgroundColor: styleForm['stroke'] || '#FFF'
                                    }}
                                />
                                <span>{t('ticket.charts.zone_editor.sidebar.form.stroke_label')}</span>
                            </div>
                            <ColorPicker
                                className="color-picker-container-block"
                                show={showPickerStroke}
                                disableAlpha
                                onChange={color => setStyle('stroke', color.hex)}
                                onClickAway={() => setShowPickerStroke(false)}
                                color={styleForm['stroke'] || '#FFF'}
                            />
                        </div>
                    </div>
                    <div className={Styles['counter-container']}>
                        <CounterLabel
                            label={t('ticket.charts.zone_editor.sidebar.form.stroke_width_label')}
                            placeholder={0}
                            onChange={(e, value) => setStyle('stroke_width', parseFloat(value))}
                            max={30}
                            value={styleForm['stroke_width'] || 0}
                            min={0}
                            step={1}
                            hideArrows
                        />
                        <CounterLabel
                            label={t('ticket.charts.zone_editor.sidebar.form.opacity_label')}
                            placeholder={0}
                            max={1}
                            value={styleForm['opacity']}
                            min={0}
                            numberDecimals={styleForm['opacity'] === 1 ? 0 : 1}
                            step={0.1}
                            onChange={(e, value) => setStyle('opacity', toFloat(value))}
                            hideArrows
                        />
                    </div>
                </div>
                <div className={Styles['color-container']}>
                    <div className={Styles['color-title']}>{t('ticket.charts.zone_editor.sidebar.hover_title')}</div>
                    <div className={Styles['color-picker-container']}>
                        <div className={Styles['color-picker']}>
                            <div className={Styles['color-picker-title-container']}>
                                <div
                                    className={Styles['color-picker-title']}
                                    onClick={() => setShowPickerHoverColor(!showPickerHoverColor)}
                                    style={{
                                        backgroundColor: styleForm['hover_color'] || '#FFF'
                                    }}
                                />
                                <span>{t('ticket.charts.zone_editor.sidebar.form.color_label')}</span>
                            </div>
                            <ColorPicker
                                className="color-picker-container-block"
                                show={showPickerHoverColor}
                                disableAlpha
                                onChange={color => setStyle('hover_color', color.hex)}
                                onClickAway={() => setShowPickerHoverColor(false)}
                                color={styleForm['hover_color'] || '#FFF'}
                            />
                        </div>
                        <div className={Styles['color-picker']}>
                            <div className={Styles['color-picker-title-container']}>
                                <div
                                    className={Styles['color-picker-title']}
                                    onClick={() => setShowPickerHoverStroke(!showPickerHoverStroke)}
                                    style={{
                                        backgroundColor: styleForm['hover_stroke'] || '#FFF'
                                    }}
                                />
                                <span>{t('ticket.charts.zone_editor.sidebar.form.stroke_label')}</span>
                            </div>
                            <ColorPicker
                                className="color-picker-container-block"
                                show={showPickerHoverStroke}
                                disableAlpha
                                onChange={color => setStyle('hover_stroke', color.hex)}
                                onClickAway={() => setShowPickerHoverStroke(false)}
                                color={styleForm['color'] || '#FFF'}
                            />
                        </div>
                    </div>
                    <div className={Styles['counter-container']}>
                        <CounterLabel
                            label={t('ticket.charts.zone_editor.sidebar.form.stroke_width_label')}
                            placeholder={0}
                            onChange={(e, value) => setStyle('hover_stroke_width', parseFloat(value))}
                            max={30}
                            value={styleForm['hover_stroke_width'] || 0}
                            min={0}
                            step={1}
                            hideArrows
                        />
                        <CounterLabel
                            label={t('ticket.charts.zone_editor.sidebar.form.opacity_label')}
                            placeholder={0}
                            onChange={(e, value) => setStyle('hover_opacity', toFloat(value))}
                            max={1}
                            value={styleForm['hover_opacity']}
                            numberDecimals={styleForm['hover_opacity'] === 1 ? 0 : 1}
                            min={0}
                            step={0.1}
                            hideArrows
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
}
